import dayjs from "dayjs";
import { z } from "zod";

/**
 * The canonical date-only display format: `Mon D, YYYY` (e.g. `Mar 10, 2025`).
 * Accepts ISO or YYYY-MM-DD. Uses timezone-safe parsing — extracts the
 * YYYY-MM-DD portion directly to avoid interpreting UTC midnight as the
 * previous day in western timezones. Use this for every stored date-only
 * value (DOB, event dates, meeting dates, due dates).
 */
export function formatDate(dateStr: string): string {
  if (!dateStr) return "";
  const d = toDateOnly(dateStr);
  if (!d) return "";
  const [y, m, day] = d.split("-");
  return `${MONTHS[Number(m) - 1]!.slice(0, 3)} ${Number(day)}, ${y}`;
}

/**
 * Month-and-day display with no year (e.g. `Jan 15`). Timezone-safe like
 * {@link formatDate}. Reserved for the upcoming-birthday / anniversary
 * next-occurrence widgets, where the year is intentionally omitted.
 */
export function formatMonthDay(dateStr: string): string {
  if (!dateStr) return "";
  const d = toDateOnly(dateStr);
  if (!d) return "";
  const [, m, day] = d.split("-");
  return `${MONTHS[Number(m) - 1]!.slice(0, 3)} ${Number(day)}`;
}

/**
 * Month-and-year display with full month name (e.g. `March 2025`). Timezone-safe
 * like {@link formatDate}. Reserved for the "member since" widget, where the
 * day is intentionally omitted.
 */
export function formatMonthYear(dateStr: string): string {
  if (!dateStr) return "";
  const d = toDateOnly(dateStr);
  if (!d) return "";
  const [y, m] = d.split("-");
  return `${MONTHS[Number(m) - 1]} ${y}`;
}

/**
 * Normalize API date/datetime to YYYY-MM-DD for input type="date".
 * Handles full ISO (e.g. 2025-03-10T00:00:00.000Z) or date-only strings.
 */
export function toDateOnly(s: string | null | undefined): string {
  if (!s || typeof s !== "string") return "";
  const slice = s.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(slice) ? slice : "";
}

/**
 * Normalize API datetime to YYYY-MM-DDTHH:mm for input type="datetime-local" (local time).
 * Handles full ISO strings; returns empty string if invalid.
 */
export function toDateTimeLocal(s: string | null | undefined): string {
  if (!s || typeof s !== "string") return "";
  const d = dayjs(s);
  if (!d.isValid()) return "";
  return d.format("YYYY-MM-DDTHH:mm");
}

/**
 * Format a true timestamp (created/updated/last-login etc.) as a `Mon D, YYYY`
 * date in the viewer's LOCAL timezone. Unlike {@link formatDate}, this honors
 * the wall-clock instant — use it for real timestamps, not date-only values.
 */
export function formatTimestamp(s: string | null | undefined): string {
  if (!s || typeof s !== "string") return "";
  const d = dayjs(s);
  if (!d.isValid()) return "";
  return d.format("MMM D, YYYY");
}

/**
 * Format API datetime string for display (e.g. "Mar 10, 2025, 2:30 PM"). Accepts ISO.
 */
export function formatDateTime(s: string | null | undefined): string {
  if (!s || typeof s !== "string") return "";
  const d = dayjs(s);
  if (!d.isValid()) return s;
  return d.format("M/D/YYYY, h:mm A");
}

/**
 * Format a date string with a 2-digit year (e.g. "3/14/26"). Accepts ISO or YYYY-MM-DD.
 * Uses timezone-safe parsing like formatDateOnly.
 */
export function formatDateShort(dateStr: string): string {
  if (!dateStr) return "";
  const d = toDateOnly(dateStr);
  if (!d) return "";
  const [y, m, day] = d.split("-");
  return `${Number(m)}/${Number(day)}/${y.slice(2)}`;
}

const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/**
 * Format a date string as a long display date (e.g. "Tuesday, March 24, 2026").
 * Accepts ISO or YYYY-MM-DD. Uses timezone-safe parsing like formatDateOnly.
 */
export function formatDateLong(dateStr: string): string {
  if (!dateStr) return "";
  const d = toDateOnly(dateStr);
  if (!d) return "";
  const [y, m, day] = d.split("-");
  const date = new Date(Number(y), Number(m) - 1, Number(day));
  const dayName = DAYS_OF_WEEK[date.getDay()];
  const monthName = MONTHS[date.getMonth()];
  return `${dayName}, ${monthName} ${Number(day)}, ${y}`;
}

/**
 * Weekday-and-date display with no year (e.g. `Fri, Sep 4`). Timezone-safe like
 * {@link formatDate}. For choosing between a handful of nearby dates, where the
 * weekday is what the reader actually recognises and the year is redundant —
 * picking which day of a campout to attend, for instance.
 */
export function formatWeekdayMonthDay(dateStr: string): string {
  if (!dateStr) return "";
  const d = toDateOnly(dateStr);
  if (!d) return "";
  const [y, m, day] = d.split("-");
  const date = new Date(Number(y), Number(m) - 1, Number(day));
  return `${DAYS_OF_WEEK[date.getDay()]!.slice(0, 3)}, ${MONTHS[date.getMonth()]!.slice(0, 3)} ${Number(day)}`;
}

/** True when `ymd` (a YYYY-MM-DD string) names a real calendar date. */
function isRealCalendarDate(ymd: string): boolean {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m! - 1 && dt.getUTCDate() === d;
}

/**
 * The single API-boundary chokepoint for date-only values. Accepts a
 * `YYYY-MM-DD` string and transforms it to a noon-UTC ISO timestamp, so
 * services never hand-roll the `T12:00:00Z` conversion. Noon UTC is
 * timezone-safe: it cannot roll to a different day in any timezone.
 */
export const dateOnly = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Enter a valid date")
  .refine(isRealCalendarDate, "Enter a valid date")
  .transform((s) => `${s}T12:00:00.000Z`);

/**
 * Optional/nullable variant of {@link dateOnly} for date-only fields that may be
 * blank or absent (admin contact/member birthdays, event end dates, due dates,
 * tenure end dates). A cleared field (`""`) becomes `null`; an omitted field
 * (`undefined`) stays `undefined` so partial updates don't clobber the stored
 * value; a present `YYYY-MM-DD` is normalized to noon UTC. No age gate.
 */
export const dateOnlyNullable = z
  .preprocess((v) => (v === "" ? null : v), dateOnly.nullable())
  .optional();

/**
 * Timezone-safe "is this birthday at least 21 years before now?" check.
 *
 * Accepts a date-only (`YYYY-MM-DD`) or ISO string and compares calendar dates
 * using the UTC calendar only — it never reads local-time getters, so the
 * accept/reject decision is identical on any server or browser timezone.
 */
export function isAtLeast21(value: string, now: Date = new Date()): boolean {
  const ymd = toDateOnly(value);
  if (!ymd) return false;
  const [by, bm, bd] = ymd.split("-").map(Number);
  const ny = now.getUTCFullYear();
  const nm = now.getUTCMonth() + 1;
  const nd = now.getUTCDate();
  let age = ny - by!;
  if (nm < bm! || (nm === bm && nd < bd!)) age--;
  return age >= 21;
}

/**
 * Date-only birthday with a timezone-safe 21+ age gate, for self-signup and
 * complete-account flows. Transforms to a noon-UTC ISO timestamp like `dateOnly`.
 */
export const birthday = dateOnly.refine((iso) => isAtLeast21(iso), {
  message: "You must be at least 21 years old",
});

export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const formatNextYear = () => {
  return dayjs().year() + 1;
};

/**
 * True once an event's last day has passed — `endDate` if set, else
 * `startDate` — compared by calendar date (UTC), not time-of-day, so an
 * event is still "not past" through the end of its final day. Mirrors the
 * upcoming/past convention used by event list filters (`::date < now()::date`).
 * An event with neither date set is never considered past.
 */
export function isEventPast(
  startDate: Date | string | null | undefined,
  endDate: Date | string | null | undefined,
  now: Date = new Date(),
): boolean {
  const reference = endDate ?? startDate;
  if (!reference) return false;
  const referenceDate = reference instanceof Date ? reference : new Date(reference);
  if (Number.isNaN(referenceDate.getTime())) return false;

  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const eventDay = Date.UTC(
    referenceDate.getUTCFullYear(),
    referenceDate.getUTCMonth(),
    referenceDate.getUTCDate(),
  );
  return eventDay < today;
}
